import { FloatingFormItem } from '@/components/_common';
import { ManifestationRecipientInvoice } from '@/core/domain/invoice/recipientInvoice/recipientInvoice.types';
import { useManifestNotes } from '@/core/domain/invoiceTransmissionHub/AuxiliaryDocuments/AuxiliaryDocuments.hooks';
import {
  ManifestNotesFormValues,
  ManifestNotesType,
} from '@/core/domain/invoiceTransmissionHub/AuxiliaryDocuments/AuxiliaryDocuments.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { DateTimeFormat } from '@/utils/moment';
import {
  Col,
  Form,
  Input,
  List,
  Modal,
  ModalProps,
  Row,
  Select,
  Typography,
} from 'antd';
import { AxiosError } from 'axios';
import moment from 'moment';

interface Props extends Omit<ModalProps, 'onCancel'> {
  companyId?: number;
  onCancel: VoidFunction;
  manifestationItems: ManifestationRecipientInvoice[];
}

export default function ManifestSelectedItemsModal({
  onCancel,
  companyId,
  manifestationItems,
  ...props
}: Props) {
  const { Text } = Typography;

  const [form] = Form.useForm<ManifestNotesFormValues>();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const manifestNotes = useManifestNotes();

  const manifestType = Form.useWatch('type', form);

  const manifestTypeOptions = [
    {
      value: ManifestNotesType.confirmedOperation,
      label: 'Confirmação da Operação',
    },
    { value: ManifestNotesType.confirmed, label: 'Ciência da Emissão' },
    {
      value: ManifestNotesType.unknown,
      label: 'Desconhecimento da Operação',
    },
    {
      value: ManifestNotesType.notExecuted,
      label: 'Operação não Realizada',
    },
  ];

  function handleCancel() {
    form.resetFields();
    onCancel();
  }

  async function handleOk() {
    try {
      const values = await form.validateFields();

      if (manifestationItems.length && companyId) {
        await manifestNotes.mutateAsync({
          companyId: companyId,
          eventDh: moment().format(DateTimeFormat),
          manifestarRequestDTO: manifestationItems.map((item) => ({
            recipientInvoiceId: item.id,
            reason: values.reason,
            type: values.type,
          })),
        });
      }

      handleCancel();
    } catch (error: unknown) {
      const err = error as AxiosError;
      if (err.isAxiosError) {
        if (!!err.response?.data.message) {
          const messageError = String(err.response?.data.message)
            .slice(1, -1)
            .split(', ');

          Modal.confirm({
            width: '80%',
            title: 'Inconsistências em algumas notas ao manifestar',
            content: (
              <List
                size="small"
                bordered
                dataSource={messageError}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              />
            ),
            okText: 'Fechar',
            cancelButtonProps: { hidden: true },
          });
        }
      }
    }
  }

  return (
    <Modal
      width={400}
      title="Manifestar notas fiscais selecionadas"
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={manifestNotes.isLoading}
      {...props}
    >
      <Form form={form} style={{ marginBottom: '2em' }}>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            Selecione o tipo de manifestação e descreva o motivo
          </Text>
        </div>

        <Row gutter={[12, 5]}>
          <Col span={24}>
            <FloatingFormItem
              name="type"
              label="Tipo"
              rules={[defaultEmptyFieldError]}
            >
              <Select
                allowClear
                options={manifestTypeOptions.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="reason"
              label="Motivo"
              rules={
                manifestType === ManifestNotesType.notExecuted
                  ? [
                      defaultEmptyFieldError,
                      { min: 15, message: 'Informe 15 ou mais caracteres' },
                    ]
                  : []
              }
            >
              <Input.TextArea rows={2} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
